import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Phone, Mail, Smartphone, Share2, RefreshCcw, Truck, UserCheck, AlertCircle, Headphones, TrendingUp, CheckCircle2 } from "lucide-react";
import { SectionLabel, HighlightText, ToolPill, SectionHeading } from "@/components/Bits";
import ComparisonTable from "@/components/ComparisonTable";
import IndustriesGrid from "@/components/IndustriesGrid";
import TeamCarousel from "@/components/TeamCarousel";
import FAQAccordion from "@/components/FAQAccordion";
import FinalCTA from "@/components/FinalCTA";
import { INDUSTRIES_SUPPORT } from "@/lib/content";

const SMART_FEATURES = [
  { num: "01", title: "Instant Response Drafting", desc: "AI tools help draft clear, accurate replies in seconds. Your support professional reviews, adjusts and sends responses that sound human, helpful and on-brand." },
  { num: "02", title: "Smarter Ticket Prioritization", dark: true, desc: "Tickets are categorized, tagged and routed faster so urgent issues are handled first. No buried complaints. No missed escalations." },
  { num: "03", title: "Sentiment Detection", dark: true, desc: "AI helps detect frustration early, so your support professional can respond with the right tone before a small issue turns into a lost customer." },
  { num: "04", title: "Proactive Issue Spotting", desc: "Repeated issues are spotted and flagged, giving you useful insights into product gaps, policy confusion, shipping problems or recurring customer pain points." },
];

const CHANNELS = [
  { icon: MessageCircle, title: "Live Chat Support", desc: "Fast, helpful replies that feel personal. No stiff scripts. Just real support that keeps customers moving." },
  { icon: Share2, title: "Social Media Support", desc: "Comments, DMs, mentions and customer questions across Instagram, Facebook, TikTok, LinkedIn and other channels." },
  { icon: Phone, title: "Phone & Email Support", desc: "Calm, professional support for customers who want to speak with a real person. Your tone, your escalation process." },
  { icon: Smartphone, title: "WhatsApp & Messaging", desc: "Quick conversational support on the channels customers already use. Perfect for order updates and follow-ups." },
  { icon: RefreshCcw, title: "Returns & Refunds Management", dark: true, desc: "Returns, exchanges and refunds handled with empathy and control, Customers feel heard while your policies stay protected." },
  { icon: Truck, title: "Order Tracking & Updates", dark: true, desc: "\"Where is my order?\" questions answered quickly and accurately. Customers stay informed before frustration builds." },
  { icon: UserCheck, title: "Client Onboarding Support", dark: true, desc: "New customers guided through your product, service or platform so they feel supported from day one." },
  { icon: AlertCircle, title: "Complaint Resolution", desc: "Difficult conversations handled with calm, empathy and professionalism. Protect the relationship, fix the issue." },
  { icon: Headphones, title: "Help Desk & Ticket Management", desc: "Full ticket management across your support platform and first response to final resolution. Every issue tracked." },
  { icon: TrendingUp, title: "Upselling & Retention", desc: "Trained to spot opportunities to retain customers or suggest better-fit options without sounding pushy or sales-heavy." },
];

const TOOLS = [
  { cat: "Help Desk & Ticketing", items: ["Zendesk", "Gorgias", "Freshdesk", "Intercom", "Help Scout"] },
  { cat: "Live Chat & Messaging", items: ["Intercom", "Drift", "Tawk.to", "Crisp"] },
  { cat: "WhatsApp & SMS", items: ["WhatsApp Business", "Twilio", "Sinch"] },
  { cat: "CRM", items: ["HubSpot", "Salesforce", "Zoho"] },
  { cat: "Ecommerce", items: ["Shopify", "WooCommerce", "Magento", "BigCommerce"] },
  { cat: "AI Tools", items: ["ChatGPT", "Claude", "Notion AI", "Grammarly"] },
];

const TEAM = [
  { initials: "RS", name: "Rohan S.", role: "Customer Support · 5 yrs", bio: "SaaS and tech product support specialist.", tools: ["Zendesk", "Intercom", "HubSpot", "Slack"] },
  { initials: "PJ", name: "Pooja J.", role: "Customer Support · 6 yrs", bio: "Ecommerce returns, refunds and order ops.", tools: ["Gorgias", "Shopify", "WhatsApp Biz", "Slack"] },
  { initials: "AK", name: "Aryan K.", role: "Customer Support · 4 yrs", bio: "Live chat and omnichannel coordinator.", tools: ["Intercom", "Drift", "Notion", "Loom"] },
  { initials: "MK", name: "Manisha K.", role: "Customer Support · 7 yrs", bio: "Subscription billing and retention specialist.", tools: ["Zendesk", "Stripe", "Recharge", "HubSpot"] },
  { initials: "VB", name: "Vivek B.", role: "Customer Support · 5 yrs", bio: "Phone and email — hospitality and travel.", tools: ["Freshdesk", "Twilio", "HubSpot", "Slack"] },
  { initials: "SL", name: "Sneha L.", role: "Customer Support · 6 yrs", bio: "Healthcare scheduling and intake support.", tools: ["Zendesk", "WhatsApp Biz", "Calendly", "Notion"] },
];

const FAQ = [
  { q: "What channels can your support professionals cover?", a: "Live chat, email, phone, WhatsApp, social DMs, help desk tickets and SMS. We staff to your priority channels and scale as needed." },
  { q: "How does AI make my support professional more effective?", a: "AI drafts initial replies, categorizes tickets, detects sentiment and surfaces recurring issues. Your professional reviews, adjusts and adds the human touch before sending." },
  { q: "Will my customers know they are talking to an offshore professional?", a: "No, they'll see your brand, your voice and your policies. We work as an extension of your team. We never disclose the offshore relationship unless you want us to." },
  { q: "Who is accountable if my support professional underperforms?", a: "Your named US partner. They monitor performance daily, intervene proactively, and arrange a replacement if needed at zero extra cost." },
  { q: "Can they handle sensitive situations like complaints and refunds?", a: "Yes. We train every professional on de-escalation, your refund policy boundaries and brand-voice empathy, Protecting both the customer and your bottom line." },
  { q: "What if I need support coverage across multiple timezones?", a: "We staff teams across timezones for 24/7 coverage. One engagement, multiple shifts, one US partner accountable end-to-end." },
];

export default function CustomerSupport() {
  return (
    <>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>AI-powered customer support</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 items-end">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[80px] leading-[0.95]">
              Your Customer Need <br />
              <span className="font-serif-italic font-normal">Answers.</span>
              <br />
              <HighlightText><span className="font-serif-italic font-normal">We Make Sure </span></HighlightText>
              <br />
              <span className="font-serif-italic font-normal">They Get Them.</span>
            </h1>
            <p className="mt-7 max-w-xl text-forest-400 text-[16px] leading-relaxed">
              Aidify gives you trained, AI-enabled customer support professionals who handle live chat, email, phone, WhatsApp, social media, tickets, returns, refunds and customer follow-ups with speed, care and consistency. You get a real professional working from a secure office in India, backed by US-based oversight that keeps quality, tone and accountability where they should be.
            </p>
            <div className="mt-8">
              <Link to="/contact" className="btn-primary">Get a Support Professional Today <ArrowRight size={16} /></Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="aidify-card-dark p-5" data-reveal>
              <div className="flex items-center justify-between text-xs text-cream-100/60 mb-3">
                <span>Live Support · 14:32</span>
                <span className="w-2 h-2 rounded-full bg-sage-200 animate-pulse" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="bg-forest-500/40 rounded-lg p-3 text-cream-100">Hi! Where's my order #4821?</div>
                <div className="bg-sage-200 rounded-lg p-3 text-forest-600">
                  Your order is with the carrier and out for delivery today between 2-6 PM. Tracking — here.
                  <div className="text-[10px] uppercase tracking-wider text-forest-500/70 mt-2">AI-drafted · Human-approved</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="bg-forest-500/40 rounded p-2 text-center"><div className="font-display font-extrabold text-cream-100">100%</div><div className="text-[9px] uppercase tracking-wider text-cream-100/60">US Oversight</div></div>
                <div className="bg-forest-500/40 rounded p-2 text-center"><div className="font-display font-extrabold text-cream-100">8+</div><div className="text-[9px] uppercase tracking-wider text-cream-100/60">Yrs avg</div></div>
                <div className="bg-forest-500/40 rounded p-2 text-center"><div className="font-display font-extrabold text-cream-100">15+</div><div className="text-[9px] uppercase tracking-wider text-cream-100/60">Industries</div></div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 border-y border-forest-500/10 py-5 overflow-hidden">
          <div className="flex items-center gap-8 whitespace-nowrap">
            <div className="flex gap-10 marquee-track">
              {[...["Email", "Phone", "Whatsapp", "Social", "Zendesk", "Gorgias", "Intercom", "Freshdesk"], ...["Email", "Phone", "Whatsapp", "Social", "Zendesk", "Gorgias", "Intercom", "Freshdesk"]].map((t, i) => (
                <span key={i} className="font-display font-bold text-2xl text-forest-400/40 uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SMART FEATURES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The AI advantage"
          title="Faster responses."
          italic="Smarter resolutions."
          description="Your customers do not want robotic answers. They want quick help from someone who understands the issue, knows what to say and can fix it without making them repeat themselves."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SMART_FEATURES.map((f, i) => (
            <div key={i} className={`p-6 ${f.dark ? "aidify-card-dark" : "aidify-card"}`} data-reveal>
              <div className={`text-xs uppercase tracking-[0.2em] mb-3 ${f.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>{f.num}</div>
              <h4 className={`font-display font-bold text-lg ${f.dark ? "text-cream-100" : "text-forest-600"}`}>{f.title}</h4>
              <p className={`text-sm mt-2 leading-relaxed ${f.dark ? "text-cream-100/70" : "text-forest-400"}`}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CHANNELS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="What they do"
          title="Every channel covered."
          italic="Every customer taken care of."
          description="Customers reach out in different places. Aidify makes sure someone capable, trained and supervised is there to respond from everyday questions to difficult complaints."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CHANNELS.map((s, i) => (
            <div key={i} className={`aidify-card p-5 ${s.dark ? "aidify-card-dark" : ""}`} data-reveal>
              <div className={`w-9 h-9 rounded-lg ${s.dark ? "bg-forest-500/50" : "bg-sage-200"} flex items-center justify-center mb-4`}>
                <s.icon size={16} className={s.dark ? "text-sage-200" : "text-forest-600"} />
              </div>
              <h4 className={`font-display font-bold text-sm ${s.dark ? "text-cream-100" : "text-forest-600"}`}>{s.title}</h4>
              <p className={`text-xs mt-2 leading-relaxed ${s.dark ? "text-cream-100/70" : "text-forest-400"}`}>{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 aidify-card-sage p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <p className="font-display font-bold text-forest-600 text-lg">
            Every missed message has a cost. <span className="font-serif-italic font-normal">Let's stop missing customers quickly.</span>
          </p>
          <Link to="/contact" className="btn-primary text-sm">Talk to Us <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Tools they work with"
          title="Already trained on the tools"
          italic="your support team uses daily."
          description="No weeks of teaching someone how to use your help desk. Trained across major customer support, ecommerce, CRM, messaging and AI tools."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOLS.map((cat, i) => (
            <div key={i} className="aidify-card p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-forest-400 mb-3">{cat.cat}</div>
              <div className="flex flex-wrap gap-1.5">{cat.items.map((t, j) => <ToolPill key={j}>{t}</ToolPill>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Industries we serve"
          title="Support that fits your industry."
          italic="Not generic replies from a script."
          description="Aidify matches professionals based on your industry, tools, customer type, policies and communication style."
        />
        <IndustriesGrid featured={INDUSTRIES_SUPPORT.featured} others={INDUSTRIES_SUPPORT.others} />
      </section>

      {/* COMPARISON */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The honest comparison"
          title="Not just lower cost."
          italic="A better way to run support."
          description="Hiring one person doesn't fix support if you still have to train, monitor, manage and chase them."
        />
        <ComparisonTable />
      </section>

      {/* TEAM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Meet your support pros"
          title="Real people."
          italic="Ready to represent your brand."
          description="Vetted, trained on major platforms, AI-tool enabled and supported by a management layer that keeps performance steady."
        />
        <TeamCarousel members={TEAM} />
      </section>

      {/* FAQ */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Frequently asked"
          title="Everything you wanted"
          italic="to know before the call."
        />
        <FAQAccordion items={FAQ} />
      </section>

      <FinalCTA />
    </>
  );
}
